import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const ThemeContext = createContext(null);
const KEY = 'awadhpay_theme'; // set ONLY by an explicit user choice (or a saved server preference)
const LEGACY_KEY = 'theme';   // old key that was auto-persisted with a dark default — not a user choice

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};

// Resolve the initial theme without consulting OS/browser prefers-color-scheme. Default: light.
export const resolveInitialTheme = () => {
  try {
    const explicit = localStorage.getItem(KEY);
    if (explicit === 'light' || explicit === 'dark') return explicit;
    if (localStorage.getItem(LEGACY_KEY) === 'light') return 'light'; // old explicit light choice (old default was dark)
  } catch { /* storage unavailable */ }
  return 'light';
};

const applyTheme = (theme) => {
  const root = window.document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
  root.style.colorScheme = theme;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(resolveInitialTheme);
  const persistRef = useRef(null); // set by AuthContext: (theme) => Promise

  useEffect(() => { applyTheme(theme); }, [theme]);

  // Explicit user choice: apply immediately, store locally, persist to the user's account
  const setTheme = (next) => {
    if (next !== 'light' && next !== 'dark') return;
    setThemeState(next);
    try { localStorage.setItem(KEY, next); localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
    persistRef.current?.(next);
  };
  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  // Called by AuthContext when a user session is restored/logged in
  const syncFromUser = (user, persist) => {
    persistRef.current = persist || null;
    const pref = user?.theme_preference;
    if (pref === 'light' || pref === 'dark') {
      setThemeState(pref);
      try { localStorage.setItem(KEY, pref); localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
    } else if (user) {
      // No saved preference on the account yet: keep an explicit local choice, else light
      const local = resolveInitialTheme();
      setThemeState(local);
      if (local === 'dark') persist?.(local); // carry an existing explicit dark choice onto the account
    }
  };
  const clearUser = () => { persistRef.current = null; };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark: theme === 'dark', syncFromUser, clearUser }}>
      {children}
    </ThemeContext.Provider>
  );
};
